<script>
// Item de navegação da sidebar de contexto. Apenas apresentacional: recebe um
// item ({ label, icon, badge }), estado ativo e modo recolhido; emite 'click'.
import {
  DETAILED_ANIMATION_ICONS,
  SELECT_REPLAY_DURATION_MS,
} from '@modules/global/components/button-navbar/button-navbar.constants'
import IconNoModuleAccess from '@modules/global/components/icon-no-module-access/icon-no-module-access.vue'
import navSurface from '@modules/navegacao-v2/mixins/nav-surface'

const ICON_SIZE = 19
const CHEVRON_SIZE = 14
const NO_ACCESS_ICON_SIZE = 14
// Texto do badge de módulo novo (destacado, vindo de `new: true` da nav antiga).
const BADGE_NEW = 'Novo'
// Chevron padrão do item de módulo com flyout (aponta para o painel, à direita).
const CHEVRON_DEFAULT = 'mdi-chevron-right'
// Intensidade do estado ativo: cheio (padrão) ou atenuado, usado pelo cabeçalho
// de Parte quando ele já está aberto e o destaque cheio vive no módulo da rota.
const ACTIVE_VARIANT = { FULL: 'full', SUBTLE: 'subtle' }
// Valores literais do aria-expanded. String e não Boolean: uma prop Boolean
// ausente viraria `false` e o atributo vazaria para todo item que não é acordeão.
const ARIA_EXPANDED = { TRUE: 'true', FALSE: 'false' }

export default {
  name: 'NavItem',
  components: { IconNoModuleAccess },
  mixins: [navSurface],
  props: {
    item: { type: Object, required: true },
    active: { type: Boolean, default: false },
    collapsed: { type: Boolean, default: false },
    // Subitens do módulo ({ key, label, routeName }). Quando presentes, o item
    // ganha chevron e dispara o flyout no hover (não navega via subitem aqui —
    // só emite hover-subitems; a navegação do subitem é feita pelo flyout/pai).
    subitems: { type: Array, default: () => [] },
    // Em mobile (drawer) o flyout por hover não funciona em touch — desligamos
    // o chevron e os eventos de subitens; os subitens ficam na topbar do módulo.
    mobile: { type: Boolean, default: false },
    // Chevron explícito: quando não vazio, renderiza independentemente de haver
    // subitens e de mobile. É o que habilita o acordeão de Parte no drawer.
    chevronName: { type: String, default: '' },
    activeVariant: {
      type: String,
      default: ACTIVE_VARIANT.FULL,
      validator: (v) => [ACTIVE_VARIANT.FULL, ACTIVE_VARIANT.SUBTLE].includes(v),
    },
    // Tri-estado: '' não emite o atributo; 'true'/'false' emitem o literal.
    expanded: {
      type: String,
      default: '',
      validator: (v) =>
        ['', ARIA_EXPANDED.TRUE, ARIA_EXPANDED.FALSE].includes(v),
    },
    controls: { type: String, default: '' },
    ariaLabel: { type: String, default: '' },
  },
  data() {
    return {
      ICON_SIZE,
      CHEVRON_SIZE,
      NO_ACCESS_ICON_SIZE,
      // Tracking de hover via JS (e não só :hover do CSS) porque a classe
      // is-animating precisa ser aplicada ao <icon-svg>, e o :hover do CSS não
      // consegue alternar classe em outro elemento.
      isHover: false,
      // True na janela de replay logo após o item virar ativo (tell toca 1x).
      justSelected: false,
      // Handle do timer de replay; limpo no beforeDestroy.
      selectAnimTimer: null,
    }
  },
  computed: {
    rootClass() {
      return {
        'nav-item': true,
        'nav-item--active': this.active,
        // Ativo atenuado: mantém barra, rótulo branco e ícone em destaque, mas
        // suprime o preenchimento — evita dois destaques cheios empilhados.
        'nav-item--active-subtle':
          this.active && this.activeVariant === ACTIVE_VARIANT.SUBTLE,
        'nav-item--collapsed': this.collapsed,
        // Estado de upsell (sem acesso ao módulo, cross-sell visível): item
        // aparece acinzentado, sinalizando "contrate o módulo" — espelha a nav antiga.
        'nav-item--no-access': this.noAccess,
      }
    },
    // Upsell vindo de visibleGroups (item.noAccessIcon). Item continua clicável
    // (leva ao fluxo de sem-acesso/contratação), só muda a aparência.
    noAccess() {
      return !!(this.item && this.item.noAccessIcon)
    },
    iconColor() {
      // Cor explícita: ícones mdi-* (fallback v-icon) não resolvem 'currentColor'.
      // `navFg` acompanha o fundo da sidebar — branco no escuro, escuro no claro.
      return this.active ? 'primary' : this.navFg
    },
    title() {
      return this.collapsed ? this.item.label : null
    },
    // Marca o ícone quando ele suporta a animação detalhada (parte a parte).
    // Vira a classe is-detailed no wrapper do icon-svg.
    isDetailedIcon() {
      return DETAILED_ANIMATION_ICONS.includes(this.item.icon)
    },
    // Gatilho único das animações do ícone: hover OU instante da seleção.
    iconAnimating() {
      return this.isHover || this.justSelected
    },
    // Badge "Novo" recebe estilo destacado (vs. badge genérico de contagem).
    isNewBadge() {
      return !!(this.item && this.item.badge === BADGE_NEW)
    },
    hasSubitems() {
      return !!(this.subitems && this.subitems.length)
    },
    // Flyout de subitens só no desktop (hover); em mobile o gatilho não existe.
    flyoutEnabled() {
      return this.hasSubitems && !this.mobile
    },
    chevronIcon() {
      return this.chevronName || CHEVRON_DEFAULT
    },
    // Com chevronName vazio a expressão reduz a `!collapsed && flyoutEnabled`,
    // que é a condição de antes desta prop existir.
    showChevron() {
      return !this.collapsed && (!!this.chevronName || this.flyoutEnabled)
    },
    // null faz o Vue 2 remover o atributo do DOM — é o caso neutro das três.
    ariaExpandedAttr() {
      return this.expanded || null
    },
    ariaControlsAttr() {
      return this.controls || null
    },
    ariaLabelAttr() {
      return this.ariaLabel || null
    },
  },
  watch: {
    active(isActive, wasActive) {
      // Item foi desmarcado: cancela o replay pendente e limpa o estado na hora,
      // para a classe nunca ficar presa num item que já não está ativo.
      if (!isActive) {
        this.stopSelectAnimation()
        return
      }
      if (wasActive) return
      this.playSelectAnimation()
    },
  },
  beforeDestroy() {
    this.clearSelectAnimTimer()
  },
  methods: {
    clearSelectAnimTimer() {
      if (this.selectAnimTimer) clearTimeout(this.selectAnimTimer)
      this.selectAnimTimer = null
    },
    // Reinicia a classe para que a animação do ícone toque novamente a cada
    // (re)seleção, mesmo se o item já tiver sido ativado antes.
    playSelectAnimation() {
      this.clearSelectAnimTimer()
      this.justSelected = false
      this.$nextTick(() => {
        this.justSelected = true
        this.selectAnimTimer = setTimeout(() => {
          this.justSelected = false
        }, SELECT_REPLAY_DURATION_MS)
      })
    },
    // Cancela o replay em andamento e reseta o estado imediatamente.
    stopSelectAnimation() {
      this.clearSelectAnimTimer()
      this.justSelected = false
    },
    // Hover: mantém o tracking de animação do ícone e, havendo subitens, avisa o
    // pai com a posição do item (top do item, borda direita = início do flyout).
    onMouseEnter() {
      this.isHover = true
      if (!this.flyoutEnabled) return
      const rect = this.$el.getBoundingClientRect()
      this.$emit('hover-subitems', {
        item: this.item,
        top: rect.top,
        left: rect.right,
      })
    },
    onMouseLeave() {
      this.isHover = false
      if (this.flyoutEnabled) this.$emit('leave-subitems')
    },
  },
}
</script>

<template>
  <button
    :class="rootClass"
    :title="title"
    :aria-expanded="ariaExpandedAttr"
    :aria-controls="ariaControlsAttr"
    :aria-label="ariaLabelAttr"
    type="button"
    @click="$emit('click')"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <span v-if="active && !collapsed" class="nav-item__bar" />
    <span class="nav-item__icon">
      <icon-svg
        class="navbar-main-icon"
        :class="{ 'is-detailed': isDetailedIcon, 'is-animating': iconAnimating }"
        :name="item.icon"
        :size="ICON_SIZE"
        :color="iconColor"
      />
    </span>
    <span v-if="!collapsed" class="nav-item__label">{{ item.label }}</span>
    <IconNoModuleAccess
      v-if="!collapsed && noAccess"
      class="nav-item__no-access"
      :iconSize="NO_ACCESS_ICON_SIZE"
      lockNavBarOnHover
      :module="item.label"
      content-class="nav-item-no-access-menu"
    />
    <span
      v-if="!collapsed && item.badge"
      class="nav-item__badge"
      :class="{ 'nav-item__badge--new': isNewBadge }"
      >{{ item.badge }}</span
    >
    <span v-if="showChevron" class="nav-item__chevron">
      <icon-svg :name="chevronIcon" :size="CHEVRON_SIZE" :color="navFg" />
    </span>
  </button>
</template>

<style lang="sass" scoped>
// "Tells" por ícone (sino balança, donut desenha o anel, barras sobem, etc.),
// idênticas às da nav antiga (button-navbar). O scale do wrapper .nav-item__icon
// abaixo é o "wrapper scale" e COEXISTE com a animação interna do svg.
@import '../styles/nav-icon-tells'

.nav-item
  position: relative
  display: flex
  align-items: center
  gap: 13px
  width: 100%
  box-sizing: border-box
  padding: 10px 14px
  border: 0
  border-radius: 11px
  background: transparent
  color: rgba(var(--nav-fg-rgb, 255, 255, 255), 0.66)
  font-family: var(--font-body, inherit)
  font-size: 13.5px
  font-weight: 600
  text-align: left
  cursor: pointer
  transition: background 0.15s, color 0.15s

  &:hover
    background: rgba(var(--nav-fg-rgb, 255, 255, 255), 0.05)
    color: var(--nav-fg, #fff)

  // Foco visível pelo teclado: sem isto, o cabeçalho de Parte é tabulável mas o
  // foco não se vê sobre o fundo da sidebar.
  &:focus-visible
    outline: 2px solid var(--primary)
    outline-offset: 2px

  // Animação dos ícones (recuperada da nav antiga): cresce com "salto" no hover
  // e fica maior quando selecionado. Easing com overshoot.
  &:hover .nav-item__icon
    transform: scale(1.18)

  &--active .nav-item__icon
    transform: scale(1.25)

  &--active
    color: var(--nav-fg, #fff)

    &::before
      content: ''
      position: absolute
      inset: 0
      border-radius: inherit
      background: var(--primary)
      opacity: 0.18
      pointer-events: none

  // Variante atenuada: REMOVE o preenchimento, não acrescenta cor nenhuma.
  &--active-subtle::before
    content: none

  &--collapsed
    justify-content: center
    padding: 11px 0

  // Upsell / sem acesso: item acinzentado (mantém clicável p/ fluxo de contratação).
  &--no-access
    color: rgba(var(--nav-fg-rgb, 255, 255, 255), 0.4)

    .nav-item__icon
      opacity: 0.4

  &__icon
    position: relative
    z-index: 1
    display: inline-flex
    align-items: center
    transform-origin: center
    transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)

  &__bar
    position: absolute
    left: -12px
    top: 9px
    bottom: 9px
    width: 3px
    border-radius: 0 3px 3px 0
    background: var(--primary)
    z-index: 1

  &__label
    position: relative
    z-index: 1
    flex: 1
    min-width: 0
    overflow: hidden
    text-overflow: ellipsis
    white-space: nowrap

  &__no-access
    position: relative
    z-index: 1
    display: inline-flex
    align-items: center

  &__badge
    position: relative
    z-index: 1
    padding: 2px 8px
    border-radius: 999px
    font-size: 10px
    background: rgba(var(--nav-fg-rgb, 255, 255, 255), 0.14)
    color: var(--nav-fg, #fff)

    // Badge "Novo": destaque na cor primária (sinaliza módulo recém-lançado). O
    // texto segue a primária, não o fundo da sidebar.
    &--new
      background: var(--primary)
      color: var(--text-on-primary, #fff)
      font-weight: 700

  // Indica que o módulo tem subitens (flyout no hover). Sutil; ganha opacidade
  // total no hover/ativo do item.
  &__chevron
    position: relative
    z-index: 1
    display: inline-flex
    align-items: center
    margin-left: 2px
    opacity: 0.4
    transition: opacity 0.15s

  &:hover &__chevron,
  &--active &__chevron
    opacity: 0.85
</style>

<style lang="sass">
// Global (não-scoped) de propósito: o conteúdo do v-menu do IconNoModuleAccess
// é destacado (Vuetify Detachable) para [data-app], fora da árvore desta sidebar
// — CSS scoped não alcança esse nó. A sidebar-shell usa z-index 60-70 e o
// nav-flyout 80; sem isso o popup do crown renderiza atrás da navbar.
.nav-item-no-access-menu
  z-index: 210 !important
</style>
